// ============================================
// RESET PASSWORD PAGE + i18n
// ============================================

import { createFileRoute, Link, useSearch } from '@tanstack/react-router';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Loader2, AlertCircle, CheckCircle, ArrowLeft, Eye, EyeOff } from 'lucide-react';
import { useMutation } from '@tanstack/react-query';
import { useState, useMemo } from 'react';
import { useTranslation } from '@crew-at-sea/i18n';

export const Route = createFileRoute('/reset-password')({
  component: ResetPasswordPage,
  validateSearch: (search: Record<string, unknown>) => {
    return {
      token: (search.token as string) || '',
    };
  },
});

// i18n destekli schema factory
function createResetPasswordSchema(t: (key: string) => string) {
  return z.object({
    password: z.string()
      .min(8, t('auth:validation.passwordMin8'))
      .regex(/[A-Z]/, t('auth:validation.passwordUppercase'))
      .regex(/[a-z]/, t('auth:validation.passwordLowercase'))
      .regex(/[0-9]/, t('auth:validation.passwordNumber')),
    confirmPassword: z.string(),
  }).refine((data) => data.password === data.confirmPassword, {
    message: t('auth:validation.passwordsMismatch'),
    path: ['confirmPassword'],
  });
}

type ResetPasswordFormData = z.infer<ReturnType<typeof createResetPasswordSchema>>;

// API URL
const API_URL = import.meta.env.VITE_API_URL || 'https://api.crewatsea.com/api/v1';

function ResetPasswordPage() {
  const { t } = useTranslation('auth');
  const { token } = useSearch({ from: '/reset-password' });
  const [isSuccess, setIsSuccess] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const resetPasswordSchema = useMemo(() => createResetPasswordSchema(t), [t]);

  const resetPasswordMutation = useMutation({
    mutationFn: async (data: ResetPasswordFormData) => {
      const response = await fetch(`${API_URL}/auth/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, password: data.password }),
      });
      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.message || t('resetPassword.errorDefault'));
      }
      return response.json();
    },
    onSuccess: () => {
      setIsSuccess(true);
    },
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ResetPasswordFormData>({
    resolver: zodResolver(resetPasswordSchema),
  });

  const onSubmit = (data: ResetPasswordFormData) => {
    resetPasswordMutation.mutate(data);
  };

  // Token yoksa hata göster
  if (!token) {
    return (
      <div 
        className="min-h-screen flex items-center justify-center px-4 relative"
        style={{
          backgroundImage: `url('/login-bg.webp')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat'
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-br from-maritime-900/85 to-maritime-700/80" />
        
        <div className="w-full max-w-md relative z-10">
          <div className="bg-white rounded-2xl shadow-xl p-8 text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-red-100 mb-4">
              <AlertCircle className="w-8 h-8 text-red-600" />
            </div>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">{t('resetPassword.invalidTitle')}</h2>
            <p className="text-gray-600 mb-6">
              {t('resetPassword.invalidMessage')}
            </p>
            <Link to="/forgot-password" className="btn-primary w-full inline-flex items-center justify-center">
              {t('resetPassword.requestNewLink')}
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div 
      className="min-h-screen flex items-center justify-center px-4 relative"
      style={{
        backgroundImage: `url('/login-bg.webp')`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat'
      }}
    >
      {/* Mavi overlay */}
      <div className="absolute inset-0 bg-gradient-to-br from-maritime-900/85 to-maritime-700/80" />
      
      <div className="w-full max-w-md relative z-10">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-white/10 backdrop-blur-sm mb-4">
            <img src="/favicon.svg" alt="Crew At Sea" className="w-10 h-10 brightness-0 invert" />
          </div>
          <h1 className="text-3xl font-bold text-white">Crew At Sea</h1>
          <p className="text-maritime-200 mt-1">{t('panelSubtitle.user')}</p>
        </div>

        {/* Form Kartı */}
        <div className="bg-white rounded-2xl shadow-xl p-8">
          {isSuccess ? (
            // Başarı Durumu
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-green-100 mb-4">
                <CheckCircle className="w-8 h-8 text-green-600" />
              </div>
              <h2 className="text-xl font-semibold text-gray-900 mb-2">{t('resetPassword.successTitle')}</h2>
              <p className="text-gray-600 mb-6">
                {t('resetPassword.successMessage')}
              </p>
              <Link to="/login" className="btn-primary w-full inline-flex items-center justify-center">
                {t('resetPassword.goToLogin')}
              </Link>
            </div>
          ) : (
            // Form Durumu
            <>
              <h2 className="text-xl font-semibold text-gray-900 mb-2">{t('resetPassword.title')}</h2>
              <p className="text-gray-600 text-sm mb-6">
                {t('resetPassword.subtitle')}
              </p>

              {/* Hata Mesajı */}
              {resetPasswordMutation.isError && (
                <div className="mb-4 p-3 rounded-maritime bg-red-50 border border-red-200 flex items-center gap-2 text-red-700 text-sm">
                  <AlertCircle className="w-4 h-4 flex-shrink-0" />
                  <span>
                    {resetPasswordMutation.error instanceof Error
                      ? resetPasswordMutation.error.message
                      : t('resetPassword.errorDefault')}
                  </span>
                </div>
              )}

              <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
                {/* Yeni Şifre */}
                <div>
                  <label htmlFor="password" className="label">
                    {t('resetPassword.password')}
                  </label>
                  <div className="relative">
                    <input
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      className={`input pr-10 ${errors.password ? 'input-error' : ''}`}
                      placeholder={t('resetPassword.passwordPlaceholder')}
                      {...register('password')}
                    />
                    <button
                      type="button"
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                  {errors.password && (
                    <p className="mt-1 text-xs text-red-500">{errors.password.message}</p>
                  )}
                </div>

                {/* Şifre Tekrar */}
                <div>
                  <label htmlFor="confirmPassword" className="label">
                    {t('resetPassword.confirmPassword')}
                  </label>
                  <div className="relative">
                    <input
                      id="confirmPassword"
                      type={showConfirmPassword ? 'text' : 'password'}
                      className={`input pr-10 ${errors.confirmPassword ? 'input-error' : ''}`}
                      placeholder={t('resetPassword.confirmPasswordPlaceholder')}
                      {...register('confirmPassword')}
                    />
                    <button
                      type="button"
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    >
                      {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                  {errors.confirmPassword && (
                    <p className="mt-1 text-xs text-red-500">{errors.confirmPassword.message}</p>
                  )}
                </div>

                {/* Şifre Gereksinimleri */}
                <div className="text-xs text-gray-500 bg-gray-50 p-3 rounded-lg">
                  <p className="font-medium mb-1">{t('resetPassword.requirements.title')}</p>
                  <ul className="space-y-0.5">
                    <li>• {t('resetPassword.requirements.minLength')}</li>
                    <li>• {t('resetPassword.requirements.uppercase')}</li>
                    <li>• {t('resetPassword.requirements.lowercase')}</li>
                    <li>• {t('resetPassword.requirements.number')}</li>
                  </ul>
                </div>

                {/* Gönder Butonu */}
                <button
                  type="submit"
                  className="btn-primary w-full"
                  disabled={resetPasswordMutation.isPending}
                >
                  {resetPasswordMutation.isPending ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      {t('resetPassword.submitting')}
                    </>
                  ) : (
                    t('resetPassword.submit')
                  )}
                </button>
              </form>

              {/* Giriş Sayfasına Dön */}
              <p className="mt-6 text-center text-sm text-gray-500">
                <Link to="/login" className="text-maritime-600 hover:text-maritime-700 font-medium inline-flex items-center">
                  <ArrowLeft className="w-4 h-4 mr-1" />
                  {t('forgotPassword.backToLogin')}
                </Link>
              </p>
            </>
          )}
        </div>

        {/* Footer */}
        <p className="text-center text-maritime-200 text-sm mt-8">
          {t('footer.copyright', { year: 2025 })}
        </p>
      </div>
    </div>
  );
}
